import { html, nothing, svg } from 'lit';
import { go } from '../router';
import { t } from '../i18n';
import type { CaptureTabName } from '../newcomers';
import { oneButtonOn } from '../one-button';
import { bloomFrom } from './capture-open';

/**
 * Record and Type (or, with one capture button, ticket 44, the lantern and a small quill), side by side with the same size and weight (ticket 36), each with one plain line on when to use
 * it, on the onboarding's last screen. A two-column grid keeps the buttons the same height when one label wraps.
 * Both open the record screen on their tab.
 */
export function renderWaysIn(record: string, type: string, style = 'margin-top:14px') {
  if (oneButtonOn()) {
    return html`
      <div class="ways-in ways-one" style="${style}">
        <button class="btn primary block way-listen" style="min-height:52px;text-transform:none" @click=${() => go('/#listen')}>${LANTERN}${t('ways.listen')}</button>
        <div class="muted small" style="text-align:start">${t('ways.listenLine')}</div>
        <button class="btn ghost way-quill" @click=${() => go('/#type')}>${QUILL}${t('ways.quill')}</button>
      </div>`;
  }
  const btn = (cls: string, to: string, label: string) =>
    html`<button class="btn primary block ${cls}" style="min-height:52px;text-transform:none" @click=${() => go(to)}>${label}</button>`;
  const line = (text: string) => html`<div class="muted small" style="text-align:start">${text}</div>`;
  return html`
    <div class="ways-in" style="display:grid;grid-template-columns:1fr 1fr;gap:6px 8px;${style}">
      ${btn('way-record', '/record', record)}
      ${btn('way-type', '/record#wanted', type)}
      ${line(t('ways.recordLine'))}
      ${line(t('ways.typeLine'))}
    </div>`;
}

const LANTERN = html`<svg class="lantern" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M9 3h6M10 3v2M14 3v2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><rect x="6.5" y="5" width="11" height="14" rx="4" fill="currentColor" opacity=".9"/><path d="M12 8c1.6 1.8 2 3 0 5.6-2-2.6-1.6-3.8 0-5.6z" fill="#ffd9a0"/><path d="M9 21h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
const QUILL = html`<svg class="quill" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M20 4C12 4 7 9 6 17l-2 3 3-2c8-1 13-6 13-14z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M8 16l7-7" stroke="currentColor" stroke-width="1.4"/></svg>`;
const MIC = html`<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
const PEN = html`<svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M4 20l4-1 11-11-3-3L5 16z" fill="currentColor"/><path d="M14 6l3 3" stroke="#e2c690" stroke-width="1.5"/></svg>`;

/**
 * Home's Record and Type, in its bottom panel over the town: two cards of the same size side by side, each an icon,
 * its label and its line, opening the capture panel on their tab.
 */
export function renderHomeWays(open: (tab: CaptureTabName) => void, demo = false) {
  if (oneButtonOn()) return renderFloatingWays(open, demo);
  const way = (cls: string, tab: CaptureTabName, icon: unknown, label: string, line: string) =>
    html`<button class="home-way ${cls}" @click=${() => open(tab)}><span class="ic">${icon}</span><b>${label}</b><span class="way-line">${line}</span></button>`;
  return html`
    <div class="ways-in home-ways">
      ${way('way-record', 'record', MIC, t('ways.record'), t('ways.recordLine'))}
      ${way('way-type', 'wanted', PEN, t('ways.type'), t('ways.typeLine'))}
    </div>`;
}

let emblemSeq = 0;
/** An eave over a hanging lantern with arcs of hearing (the one-button prototype's emblem); gradient ids are unique per instance. */
export function emblem() {
  const id = `emb${++emblemSeq}`;
  return svg`<svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
  <defs>
    <linearGradient id="${id}g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffe2a6"/><stop offset="1" stop-color="#ff7a59"/></linearGradient>
    <radialGradient id="${id}f" cx="50%" cy="45%" r="60%"><stop offset="0" stop-color="#fffbe6"/><stop offset="1" stop-color="#ffb15a"/></radialGradient>
  </defs>
  <path d="M4 47 C24 45 38 30 50 10 C62 30 76 45 96 47 C78 52 66 47 50 41 C34 47 22 52 4 47Z" fill="url(#${id}g)"/>
  <path d="M50 41 V50" stroke="#ffd9a0" stroke-width="2" stroke-linecap="round"/>
  <rect x="45" y="49" width="10" height="4" rx="2" fill="#ffd9a0"/>
  <rect x="40" y="52" width="20" height="28" rx="9" fill="url(#${id}f)"/>
  <circle cx="50" cy="66" r="5" fill="#fff"/>
  <path d="M28 60 q-7 9 0 18" fill="none" stroke="#ffd9a0" stroke-width="3" stroke-linecap="round" opacity=".8"/>
  <path d="M72 60 q7 9 0 18" fill="none" stroke="#ffd9a0" stroke-width="3" stroke-linecap="round" opacity=".8"/>
  <path d="M20 56 q-11 13 0 26" fill="none" stroke="#ffd9a0" stroke-width="2.4" stroke-linecap="round" opacity=".4"/>
  <path d="M80 56 q11 13 0 26" fill="none" stroke="#ffd9a0" stroke-width="2.4" stroke-linecap="round" opacity=".4"/>
</svg>`;
}

/** Taps on the lantern on this device; its line under it goes once the lantern has been opened this often. */
const LANTERN_USES = 'li-lantern-uses';
const LANTERN_LEARNT = 3;

function lanternUses(): number {
  try { return Number(localStorage.getItem(LANTERN_USES)) || 0; } catch { return 0; }
}

/**
 * Home's one capture button (ticket 44), floating over the town: the lantern as a glowing, breathing emblem with
 * ripples, "Listen" in the hand under it and a small glass quill beside it (the inline end, so mirrored right to left).
 * Its line shows until the lantern has been opened `LANTERN_LEARNT` times on this device. A tap on the lantern notes
 * where it glows, so the panel blooms out of it (`bloomFrom`).
 */
function renderFloatingWays(open: (tab: CaptureTabName) => void, demo = false) {
  const listen = (e: Event) => {
    if (demo) { open('listen'); return; } // the opening's example: no count, no bloom
    try { localStorage.setItem(LANTERN_USES, String(lanternUses() + 1)); } catch { /* private mode: the line stays */ }
    const r = (e.currentTarget as HTMLElement).querySelector('.lantern-disk')?.getBoundingClientRect();
    if (r) bloomFrom(r.left + r.width / 2, r.top + r.height / 2);
    open('listen');
  };
  return html`
    <div class="ways-in home-ways home-one floating-ways">
      <div class="lantern-row">
        <button class="way-listen" aria-label=${t('ways.listen')} @click=${listen}>
          <span class="lantern-disk" aria-hidden="true"><i class="lantern-ring"></i><i class="lantern-ring"></i><i class="lantern-ring"></i>${emblem()}</span>
          <span class="lantern-cap" aria-hidden="true">${t('ways.listen')}</span>
        </button>
        <button class="way-quill" aria-label=${t('ways.quill')} title=${t('ways.quill')} @click=${() => open('wanted')}><span class="quill-glass">${QUILL}</span></button>
      </div>
      ${lanternUses() < LANTERN_LEARNT ? html`<div class="way-line">${t('ways.listenLine')}</div>` : nothing}
    </div>`;
}
